import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";

import { Text, View } from "../base";
import { IconButton } from "../base/IconButton";

const drawerVariants = {
  hidden: { x: "100%", opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: "tween",
      duration: 0.3,
      ease: "easeInOut",
    },
  },
  exit: {
    x: "100%",
    opacity: 0,
    transition: {
      type: "tween",
      duration: 0.3,
      ease: "easeInOut",
    },
  },
};

type AdminDetailDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  width?: number;
  headerActions?: React.ReactNode;
  /** Close when a click lands outside the drawer. Opt-in: pages that switch
   *  selection by clicking other table rows still work (the row's click
   *  handler runs after this mousedown listener, re-opening on the new row),
   *  but callers layering dialogs above the drawer should pass false while
   *  a dialog is open so clicks inside it don't close the drawer beneath. */
  closeOnOutsideClick?: boolean;
  children: React.ReactNode;
};

export const AdminDetailDrawer = ({ isOpen, onClose, title, subtitle, width = 440, headerActions, closeOnOutsideClick, children }: AdminDetailDrawerProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !closeOnOutsideClick) return;
    const onMouseDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isOpen, closeOnOutsideClick, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Defer: dialog/palette listeners on window fire after this one and
      // call preventDefault() when they consume Escape. Only close the
      // drawer if nothing above it claimed the event.
      window.setTimeout(() => {
        if (!e.defaultPrevented) onClose();
      }, 0);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <motion.div
      ref={panelRef}
      variants={drawerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="fixed right-0 top-0 z-50 flex h-screen flex-col border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-slate-900"
      style={{ width: `min(${width}px, 100vw)` }}
    >
      <View className="flex flex-row items-start justify-between gap-3 border-b border-gray-200 dark:border-gray-800 px-5 py-4">
        <View className="flex min-w-0 flex-col">
          <Text className="block text-base font-semibold text-gray-900 dark:text-white truncate">{title}</Text>
          {subtitle && <Text className="block text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{subtitle}</Text>}
        </View>
        <View className="flex shrink-0 flex-row items-center gap-1">
          {headerActions}
          <IconButton
            icon="close"
            variant="info"
            onClick={onClose}
            title="Close"
          />
        </View>
      </View>
      <div className="flex-1 overflow-y-auto p-5 space-y-6">{children}</div>
    </motion.div>
  );
};
